import moment from "moment";
import Copy from "../../copy";
import { StatusBadge } from "@/pages/dashboard/_misc/columns";
import { useGetBIN, useGetRate } from "@/hooks/use-queries";
import TopupCountdown from "@/components/topup-countdown";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";
import { useUser } from "@/zustand/store";
import {
  CARD_TRANSACTION_LABELS,
  // Card amounts are shown in the card's own currency (USD), never converted to
  // the viewer's preferred currency — so use the plain formatter, not the
  // `useFormatAmountWithCurrency` hook that the wallet section uses.
  formatAmount as formatPlainAmount,
  getCardTransactionDirection,
  getCardTransactionDisplayAmount,
  getCardTransactionSign,
} from "@/lib/utils";

// ── Wallet transaction types the API actually returns ──────────────────────
type WalletType =
  | "deposit"
  | "withdrawal"
  | "transfer"
  | "internal-transfer"
  | "card-topup"
  | "card-creation"
  | "card-withdrawal"
  | "referral-reward";

const typeMap: Record<WalletType, { title: string; icon: string }> = {
  deposit: {
    title: "Deposit",
    icon: "/icons/deposit.svg",
  },
  withdrawal: {
    title: "Withdrawal",
    icon: "/icons/withdrawal.svg",
  },
  transfer: {
    title: "Transfer",
    icon: "/icons/transfer.svg",
  },
  "internal-transfer": {
    title: "Internal Transfer",
    icon: "/icons/withdrawal.svg",
  },
  "referral-reward": {
    title: "Referral Reward",
    icon: "/icons/deposit.svg",
  },
  "card-topup": {
    title: "Card Funding",
    icon: "/icons/deposit.svg",
  },
  "card-creation": {
    title: "Card Creation",
    icon: "/icons/card.svg",
  },
  "card-withdrawal": {
    title: "Card Withdrawal",
    icon: "/icons/withdrawal.svg",
  },
};

// Fields not relevant for card-creation and card-withdrawal transactions
const CARD_SIMPLE_TYPES: WalletType[] = ["card-creation", "card-withdrawal"];

// ── Bin lookup sub-component (needs its own hook call) ─────────────────────
const CardTopupBinInfo = ({ binId }: { binId?: number | string }) => {
  const { data: binsData } = useGetBIN();
  const bins = Array.isArray(binsData) ? binsData : (binsData?.data ?? []);
  const matched = bins.find((b: any) => String(b.id) === String(binId));

  return (
    <>
      <DetailRow label="Type" value="Card Funding" />
      <DetailRow label="Bin" value={matched?.bin || "-"} />
      <DetailRow label="Card Network" value={matched?.network || "-"} />
    </>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const TransactionDetails = ({
  transactionData,
  type,
}: {
  transactionData: Transaction | CardTransaction;
  type: string;
}) => {
  const formatAmount = useFormatAmountWithCurrency();
  const user = useUser((state) => state.user);
  const { data: rates } = useGetRate(user?.preferredCurrency || "USD");

  const walletTransaction = transactionData as Omit<Transaction, "type"> & {
    type: WalletType;
    binId?: number | string;
  };
  const cardTransaction = transactionData as CardTransaction;

  const isCardSimple = CARD_SIMPLE_TYPES.includes(walletTransaction.type);
  const isCardTopup = walletTransaction.type === "card-topup";

  if (type === "wallet")
    return (
      <div>
        <div className="text-dark-text-400 mt-4 px-2">
          <div className="mt-4 space-y-2.5">
            <TransactionType type={walletTransaction.type} />
            <h5 className="text-2xl font-semibold">
              {formatAmount(walletTransaction.amount)}
            </h5>
          </div>

          <div className="mt-4 mb-10">
            <DetailRow label="Id" value={walletTransaction.id} />

            {/* Type / Bin / Card Network — card-topup only, placed right after Id */}
            {isCardTopup && (
              <CardTopupBinInfo binId={walletTransaction.binId} />
            )}

            {/* Network — hidden for card-creation / card-withdrawal / card-topup */}
            {!isCardSimple && !isCardTopup && (
              <DetailRow
                label="Network"
                value={walletTransaction.network || "-"}
              />
            )}

            <DetailRow
              label="Amount"
              value={String(formatAmount(walletTransaction.amount || 0))}
            />

            {/* Currency pair & exchange rate — only for deposit/withdrawal/transfer */}
            {!isCardSimple && !isCardTopup && (
              <>
                <DetailRow
                  label={`Exchange rate`}
                  value={`1 ${user?.preferredCurrency || "USD"} = ${parseFloat(rates?.rate).toFixed(2)} USDT`}
                />
              </>
            )}

            <DetailRow
              label={`Fee (${walletTransaction.feePercent || 0}%)`}
              value={`${formatAmount(walletTransaction.feeAmount || 0)}`}
            />

            {walletTransaction.type !== "card-creation" &&
              !isCardTopup &&
              (walletTransaction.type === "withdrawal" ||
              walletTransaction.type === "card-withdrawal" ? (
                <DetailRow
                  label="Amount Sent"
                  valueComponent={
                    <span className="text-[#FF6E7A]">
                      -{formatAmount(walletTransaction.amountReceived)}
                    </span>
                  }
                />
              ) : (
                <DetailRow
                  label="Amount Received"
                  valueComponent={
                    <span className="text-[#77FF9E]">
                      +{formatAmount(walletTransaction.amountReceived)}
                    </span>
                  }
                />
              ))}

            {walletTransaction.type !== "card-creation" && !isCardTopup && (
              <DetailRow
                label="Destination"
                copy
                value={walletTransaction.address || "-"}
              />
            )}

            {/* TxId — hidden for card-creation / card-withdrawal / card-topup */}
            {!isCardSimple && !isCardTopup && (
              <DetailRow
                label="TxId"
                copy
                value={walletTransaction.transactionHash || "-"}
                valueComponent={
                  <div className="flex gap-4">
                    {walletTransaction.type !== "deposit" ||
                      (walletTransaction.type === "deposit" &&
                        walletTransaction.transactionHash !== null && (
                          <span>
                            {walletTransaction.transactionHash || "-"}
                          </span>
                        ))}
                  </div>
                }
              />
            )}

            <DetailRow label="Status" value={walletTransaction.status} />
            <DetailRow
              label="Date"
              value={moment(walletTransaction.createdAt).format(
                "DD MMM YYYY, hh:mm A",
              )}
            />

            {isCardTopup && walletTransaction.status === "pending" && (
              <TopupCountdown
                createdAt={walletTransaction.createdAt}
                transactionId={walletTransaction.id}
                amount={Number(walletTransaction.amount)}
                currency={walletTransaction.currency ?? undefined}
              />
            )}
          </div>
        </div>
      </div>
    );

  // ── Card transaction (TransferIn / TransferOut / Fee_Consumption) ──────
  const { amount: cardDisplayAmount, currency: cardDisplayCurrency } =
    getCardTransactionDisplayAmount(cardTransaction);
  const cardAmountText = `${formatPlainAmount(cardDisplayAmount)} ${cardDisplayCurrency}`;
  const cardDirection = getCardTransactionDirection(cardTransaction.type);
  const cardSign = getCardTransactionSign(
    cardTransaction.type,
    cardDisplayAmount,
    cardTransaction.status,
  );

  return (
    <div>
      <div className="text-dark-text-400 mt-4 px-2">
        <div className="mt-4 space-y-2.5">
          <CardTransactionType type={cardTransaction.type} />
          <h5 className="text-2xl font-semibold">{cardAmountText}</h5>
        </div>

        <div className="mt-4 mb-10">
          <DetailRow
            label="Id"
            value={cardTransaction.transactionId || cardTransaction.id}
          />
          <DetailRow
            label="Type"
            value={
              CARD_TRANSACTION_LABELS[cardTransaction.type] ||
              cardTransaction.type
            }
          />
          <DetailRow label="Amount" value={cardAmountText} />
          {cardSign !== "" && (
            <DetailRow
              label={cardDirection === "in" ? "Amount Received" : "Amount Sent"}
              valueComponent={
                <span
                  className={
                    cardDirection === "in"
                      ? "text-[#77FF9E]"
                      : "text-[#FF6E7A]"
                  }
                >
                  {cardSign}
                  {cardAmountText}
                </span>
              }
            />
          )}
          <DetailRow
            label="Merchant"
            value={cardTransaction.merchantName || "-"}
          />
          <DetailRow label="Status" value={cardTransaction.status} />
          <DetailRow
            label="Date"
            value={moment(cardTransaction.transactionTime).format(
              "DD MMM YYYY, hh:mm A",
            )}
          />
        </div>
      </div>
    </div>
  );
};

// ── Shared UI pieces ───────────────────────────────────────────────────────

interface DetailRowProps {
  label: string;
  value?: string | number;
  valueComponent?: React.ReactNode;
  copy?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({
  label,
  value,
  valueComponent,
  copy = false,
}) => {
  if (label === "Status")
    return (
      <div className="flex items-center justify-between border-black/10 pt-2 pb-2">
        <div className="text-xs capitalize">{label}</div>
        <StatusBadge
          status={value != null ? String(value) : undefined}
          className="bg-transparent !px-0"
        />
      </div>
    );
  return (
    <div className="flex items-center justify-between border-black/10 pt-2 pb-2">
      <div className="text-xs capitalize">{label}</div>
      <div className="flex items-center gap-2">
        {copy && value && value !== "-" && (
          <Copy
            icon="/icons/copy-light.svg"
            side="left"
            text={value as string}
          />
        )}
        <span className="max-w-70 text-right text-sm font-medium break-words capitalize">
          {valueComponent ? valueComponent : value}
        </span>
      </div>
    </div>
  );
};

const TransactionType = ({ type }: { type: WalletType }) => {
  const entry = typeMap[type];
  if (!entry) return null;
  return (
    <div className="flex items-center gap-2">
      <div className="bg-dark-primary-50 flex size-8 items-center justify-center rounded-full">
        <img className="size-4" src={entry.icon} alt={entry.title} />
      </div>
      <span className="font-semibold uppercase">{entry.title}</span>
    </div>
  );
};

// Icon only — the label comes from CARD_TRANSACTION_LABELS. Inbound money uses
// the deposit glyph, everything else the withdrawal glyph.
const cardTypeIcon = (type: string) =>
  getCardTransactionDirection(type) === "in"
    ? "/icons/deposit.svg"
    : "/icons/withdrawal.svg";

const CardTransactionType = ({ type }: { type: string }) => {
  const title = CARD_TRANSACTION_LABELS[type] || type;
  return (
    <div className="flex items-center gap-2">
      <div className="bg-dark-primary-50 flex size-8 items-center justify-center rounded-full">
        <img className="size-4" src={cardTypeIcon(type)} alt={title} />
      </div>
      <span className="font-semibold uppercase">{title}</span>
    </div>
  );
};

export default TransactionDetails;
